// RecurringDetailTabs — real data renderers for the recurring-donation detail
// sub-resource tabs (RDM-R08).
//
// Each component fetches a REAL backend endpoint and renders actual rows
// (previously these were placeholder cards citing — and in two cases
// MIS-citing — endpoints). Endpoints used (all exist; see
// src/api/app/routers/recurring_donations.py):
//   Charges        → GET /recurring-donations/{id}/charges
//   Communications → GET /recurring-donations/{id}/delivery-logs
//
// The Schedule and Donor tabs were REMOVED from the detail config: there is no
// forward-schedule projection endpoint, and the /recurring-donations/{id}/donor
// route never existed. The donor is reachable via the Donor link in the
// overview tab (navigates to /donors/{donor_id}, same as v1).

import { useQuery } from "@tanstack/react-query";
import { DataTable, type DataTableColumn } from "../composition/DataTable";
import { ACISurface } from "../aci/ACISurface";
import { useFormatters } from "../../contexts/IntlContext";
import { useHasPermission } from "../../auth/usePermissions";
import { recurringDonationsApi } from "../../api/recurring-donations";
import type {
  RecurringChargeSummary,
  RecurringDeliveryLogEntry,
} from "../../api/types";

// ── ACI intent-provenance surface (gated) ────────────────────────────
// RDM-R08 — mirrors the v1 dashboard gate (CHG-aci-v1.5): the
// recurring-intent-provenance ACI surface mounts ONLY when the row carries an
// intent_key AND the operator has aci.kb.read. Previously the v2 detail
// rendered ACISurface unconditionally on every recurring donation, including
// non-intent rows the operator may not be permissioned to see context for.
// Returns null (renders nothing) when either gate fails.
export function RecurringAciSurface({
  recurringId,
  intentKey,
}: {
  recurringId: string;
  intentKey: string | null | undefined;
}) {
  const canReadAciKb = useHasPermission("aci.kb.read");
  if (!intentKey || !canReadAciKb) return null;
  return <ACISurface surface_id="recurring-intent-provenance" entity_id={recurringId} />;
}

// ── Charges tab ──────────────────────────────────────────────────────

export function RecurringChargesTab({ recurringId }: { recurringId: string }) {
  const fmt = useFormatters();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["recurring-donations", "charges", recurringId],
    queryFn: () => recurringDonationsApi.getRecurringCharges(recurringId),
  });

  const columns: DataTableColumn<RecurringChargeSummary>[] = [
    {
      field: "charged_at",
      label: "Charged",
      type: "custom",
      render: (r) => (r.charged_at ? fmt.formatDate(r.charged_at, "long") : "—"),
    },
    {
      field: "amount_minor_units",
      label: "Amount",
      type: "custom",
      render: (r) => fmt.formatCurrency(r.amount_minor_units, r.currency),
    },
    { field: "status", label: "Status", type: "custom", render: (r) => r.status },
    {
      field: "failure_reason",
      label: "Detail",
      type: "custom",
      render: (r) => r.failure_reason ?? "—",
    },
  ];

  return (
    <DataTable<RecurringChargeSummary>
      data={data?.items ?? []}
      columns={columns}
      isLoading={isLoading}
      error={error ? new Error("Could not load charge history.") : null}
      onRetry={() => void refetch()}
      emptyState={{
        title: "No charges yet",
        subtitle: "Completed and attempted charges appear here once the first cycle runs.",
      }}
    />
  );
}

// ── Communications tab ───────────────────────────────────────────────

export function RecurringCommunicationsTab({ recurringId }: { recurringId: string }) {
  const fmt = useFormatters();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["recurring-donations", "delivery-logs", recurringId],
    queryFn: () => recurringDonationsApi.getRecurringDeliveryLogs(recurringId),
  });

  const columns: DataTableColumn<RecurringDeliveryLogEntry>[] = [
    {
      field: "sent_at",
      label: "Sent",
      type: "custom",
      render: (r) => (r.sent_at ? fmt.formatDate(r.sent_at, "long") : "—"),
    },
    { field: "channel", label: "Channel", type: "custom", render: (r) => r.channel },
    { field: "template_slug", label: "Template", type: "custom", render: (r) => r.template_slug },
    { field: "recipient", label: "Recipient", type: "custom", render: (r) => r.recipient },
    { field: "status", label: "Status", type: "custom", render: (r) => r.status },
  ];

  return (
    <DataTable<RecurringDeliveryLogEntry>
      data={data?.items ?? []}
      columns={columns}
      isLoading={isLoading}
      error={error ? new Error("Could not load communications.") : null}
      onRetry={() => void refetch()}
      emptyState={{
        title: "No communications yet",
        subtitle: "Receipts, renewal reminders, and recovery messages sent for this donation appear here.",
      }}
    />
  );
}
